import { createSelector } from 'reselect';
import values from 'lodash/fp/values';
import contentTypes from 'config/types/content';
import { getContentItemPath } from './createGetContentItem';
import getPathMap from './getPathMap';
import getMatch from './getMatch';

/**
 * Get menu items from a menu in a particular position
 *
 * @param {object} state - current app state
 * @param {string} position - menu position name
 */
const getMenu = createSelector(
  [
    (state, position) => state.menu[position] || [],
    getPathMap,
    getMatch,
  ],
  (menu, pathMap, match) => {
    const assignUrl = (menuItem) => ({
      ...menuItem,
      url: menuItem.fullUrl || getPathByItemRef(pathMap, menuItem, match),
    });
    return menu.map(assignUrl);
  },
);
export default getMenu;

/**
 * Get the submenu items under a top-level item with a specified slug
 *
 * @param {object} state - current app state
 * @param {string} position - menu position name
 * @param {number} id - id of top-level menu item for which you want submenu items
 */
export const getSubNav = (state, position, id) => {
  const menu = getMenu(state, position);

  return menu.filter(({ parentId }) => id === parentId);
};

/**
 * Get menu items from a menu in a particular position,
 * with all submenu items nested in properties under their parent
 *
 * @param {object} state - current app state
 * @param {string} position - menu position name
 */
export const getMenuHierarchical = (state, position) => {
  const menu = getMenu(state, position);

  return menu
    .map((item) => ({
      subNav: getSubNav(state, position, item.id),
      ...item,
    }))
    .filter(filterTopLevel);
};

/**
 * Get menu items from a menu in a particular position,
 * with all submenu items filtered out
 *
 * @param {object} state - current app state
 * @param {string} position - menu position name
 */
export const getMenuTopLevel = (state, position) => {
  const menu = getMenu(state, position);

  return menu.filter(filterTopLevel);
};

function filterTopLevel({ parentId }) {
  return !parentId;
}

/**
 * Shim menu item content item references into a compatible content item shape subset,
 * and return value of getContentItemPath.
 *
 * TODO: postType is WP specific terminology and should be changed to contentType if possible.
 * @param {object} pathMap
 * @param {string} postType
 * @param {string} slug
 * @returns {string}
 */
function getPathByItemRef(pathMap, { postType, slug }, match) {
  if (!postType || !values(contentTypes).includes(postType)) {
    return '';
  }

  return getContentItemPath(pathMap, {
    metadata: { type: postType },
    slug,
  }, match);
}
